import { Fragment, useMemo, useState } from 'react'
import { useApp } from '../state/AppStore'
import { useAllEvents } from '../state/useAllEvents'
import { buildSeasonStats, type SeasonStats } from '../domain/stats'
import { orderStatTypes, statType } from '../domain/statTypes'
import { BarRow, TrendChart, rollingAverage } from '../components/Charts'
import { formatDate } from '../lib/date'
import { Recap } from './Recap'
import { Season } from './Season'
import { gameResult } from '../domain/derive'
import type { ID, StatTypeId } from '../domain/types'

/**
 * Stats.
 *
 * Two things this screen refuses to do. It never claims significance: a social
 * league season is a handful of games, almost any gap between two averages is
 * noise, and every panel that compares anything says how many games it rests on.
 * And it never shows PlayHQ points or fouls, because the whole point of the app
 * is the contributions the scoreboard does not record.
 */
export function Stats() {
  const { games, players, currentSeason, snapshot } = useApp()
  const { events, loading } = useAllEvents()
  const [focus, setFocus] = useState<ID | null>(null)
  const [recapGameId, setRecapGameId] = useState<ID | null>(null)
  const [showSeason, setShowSeason] = useState(false)

  // Games with a recap: finished, and from a PlayHQ fixture so a server-side
  // recap exists (a manual game has only a local id and no server row).
  const recapGames = useMemo(
    () =>
      games
        .filter((g) => g.status === 'final' && g.playhqGameId)
        .sort((a, b) => b.date.localeCompare(a.date)),
    [games],
  )

  const statIds = useMemo(() => orderStatTypes(currentSeason.enabledStatTypes), [currentSeason.enabledStatTypes])
  const stats = useMemo(
    () => buildSeasonStats(games, players, events, snapshot?.snapshot ?? null, statIds),
    [games, players, events, snapshot, statIds],
  )

  if (loading) return <div className="panel"><p className="muted">Reading your games...</p></div>

  if (recapGameId) return <Recap gameId={recapGameId} onClose={() => setRecapGameId(null)} />
  if (showSeason) return <Season onClose={() => setShowSeason(false)} />

  // Available even with no LOCAL games: a watcher's device has none, but the
  // server-side Season So Far still has the team's season.
  const seasonButton = (
    <button type="button" className="row row--button" onClick={() => setShowSeason(true)}>
      <div className="row__label">
        <span className="row__title">Season So Far</span>
        <span className="row__meta">Leaderboards, trends, the ladder and what is next</span>
      </div>
      <span className="row__go">Open →</span>
    </button>
  )

  if (stats.gamesScored === 0) return <EmptyStats seasonButton={seasonButton} />

  return (
    <div className="stats">
      <SampleNote stats={stats} />
      {seasonButton}
      {recapGames.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">Match recaps</h2>
          <div className="rows">
            {recapGames.map((g) => {
              const r = gameResult(g)
              return (
                <button key={g.id} type="button" className="row row--button" onClick={() => setRecapGameId(g.id)}>
                  <div className="row__label">
                    <span className="row__title">
                      {g.roundAbbreviation ? `${g.roundAbbreviation} · ` : ''}
                      {g.homeAway === 'home' ? 'vs' : '@'} {g.opponentName ?? 'Opponent'}
                    </span>
                    <span className="row__meta">
                      {formatDate(g.date)}
                      {r ? ` · ${r === 'W' ? 'Won' : r === 'L' ? 'Lost' : 'Drew'} ${g.ourScore}–${g.theirScore}` : ''}
                    </span>
                  </div>
                  <span className="row__go">Recap →</span>
                </button>
              )
            })}
          </div>
        </section>
      )}
      {stats.insights.length > 0 && <Insights stats={stats} />}
      <PlayerTable stats={stats} focus={focus} onFocus={setFocus} />
      {focus && <PlayerDetail stats={stats} playerId={focus} onClose={() => setFocus(null)} />}
      <TeamTrend stats={stats} />
      <div className="stats__cols">
        {stats.winLoss && <WinLoss stats={stats} />}
        {stats.periods.length > 1 && <Periods stats={stats} />}
      </div>
      <Opponents stats={stats} />
    </div>
  )
}

function EmptyStats({ seasonButton }: { seasonButton?: React.ReactNode }) {
  const { snapshot } = useApp()
  const ladder = snapshot?.snapshot.ladder ?? []
  const us = ladder.find((r) => r.is_us)
  return (
    <div className="stats">
      {seasonButton}
      <section className="panel">
        <h2 className="panel__title">Stats</h2>
        <p className="placeholder__lead">Nothing tracked yet</p>
        <p>
          These fill in as you score games. Rebounds, assists and steals are recorded in this app. Results
          and the ladder come from PlayHQ.
        </p>
        <p className="muted small">
          One game is enough to see totals. Comparisons like wins against losses stay hidden until there
          is enough to be worth showing.
        </p>
      </section>
      {us && (
        <section className="panel">
          <h2 className="panel__title">From PlayHQ</h2>
          <p>
            {us.team_name} sit {us.position} of {ladder.length} with {us.won} wins and {us.lost} losses.
          </p>
          <p className="muted small">
            That is the league's own record. It has nothing to do with the stats this app tracks.
          </p>
        </section>
      )}
    </div>
  )
}

function SampleNote({ stats }: { stats: SeasonStats }) {
  const thin = stats.gamesScored < 5
  return (
    <div className={'samplenote' + (thin ? ' samplenote--thin' : '')}>
      <strong>{stats.gamesScored}</strong> {stats.gamesScored === 1 ? 'game' : 'games'} tracked
      {stats.gamesWithResult < stats.gamesScored && (
        <> · {stats.gamesScored - stats.gamesWithResult} still awaiting a result from PlayHQ</>
      )}
      {thin && <> · too few games to read much into differences yet</>}
    </div>
  )
}

function Insights({ stats }: { stats: SeasonStats }) {
  return (
    <section className="panel">
      <h2 className="panel__title">Worth noticing</h2>
      <ul className="insights">
        {stats.insights.map((i, n) => (
          <li key={n}>
            {i.text} <span className="insights__basis">from {i.basis} games</span>
          </li>
        ))}
      </ul>
      <p className="muted small">
        Plain observations, not conclusions. With this many games a difference can easily be chance.
      </p>
    </section>
  )
}

function PlayerTable({
  stats,
  focus,
  onFocus,
}: {
  stats: SeasonStats
  focus: ID | null
  onFocus(id: ID | null): void
}) {
  const [sortBy, setSortBy] = useState<StatTypeId | 'name'>(stats.statIds[0] ?? 'name')
  const rows = useMemo(() => {
    const list = [...stats.players]
    if (sortBy === 'name') list.sort((a, b) => a.player.name.localeCompare(b.player.name))
    else list.sort((a, b) => b.totals[sortBy]! - a.totals[sortBy]! || a.player.name.localeCompare(b.player.name))
    return list
  }, [stats.players, sortBy])

  return (
    <section className="panel">
      <h2 className="panel__title">Players</h2>
      <div className="tablewrap">
        <table className="table">
          <thead>
            <tr>
              <th className="table__sort" onClick={() => setSortBy('name')}>
                Player
              </th>
              <th>GP</th>
              {stats.statIds.map((id) => (
                <th key={id} colSpan={3} style={{ color: statType(id).color }} className="table__sort" onClick={() => setSortBy(id)}>
                  {statType(id).short}
                </th>
              ))}
            </tr>
            <tr className="table__sub">
              <th />
              <th />
              {stats.statIds.map((id) => (
                <Fragment key={id}>
                  <th>tot</th>
                  <th>avg</th>
                  <th>best</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.player.id}
                className={focus === r.player.id ? 'table__row--on' : undefined}
                onClick={() => onFocus(focus === r.player.id ? null : r.player.id)}
              >
                <td className="table__name">{r.player.name}</td>
                <td>{r.gamesPlayed}</td>
                {stats.statIds.map((id) => (
                  <Fragment key={id}>
                    <td className="table__num">{r.totals[id]}</td>
                    <td className="table__num table__muted">{r.averages[id]}</td>
                    <td className="table__num table__muted">{r.highs[id]!.value}</td>
                  </Fragment>
                ))}
              </tr>
            ))}
            <tr className="table__total">
              <td className="table__name">Team</td>
              <td />
              {stats.statIds.map((id) => (
                <Fragment key={id}>
                  <td className="table__num">{stats.teamTotals[id]}</td>
                  <td className="table__num">{stats.teamAverages[id]}</td>
                  <td />
                </Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="muted small">Tap a player for their season. GP counts games they were marked present for.</p>
    </section>
  )
}

function PlayerDetail({ stats, playerId, onClose }: { stats: SeasonStats; playerId: ID; onClose(): void }) {
  const line = stats.players.find((p) => p.player.id === playerId)
  if (!line) return null
  const perGame = stats.games.map((g) => ({
    label: g.game.roundAbbreviation ?? formatDate(g.game.date).slice(0, 6),
    game: g,
  }))

  return (
    <section className="panel">
      <h2 className="panel__title">
        {line.player.name}
        <button type="button" className="btn btn--sm panel__action" onClick={onClose}>
          Close
        </button>
      </h2>

      <div className="detail">
        {stats.statIds.map((id) => {
          const def = statType(id)
          const values = perGame.map((p) => countFor(stats, p.game.game.id, playerId, id))
          return (
            <div key={id} className="detail__stat">
              <div className="detail__head">
                <span style={{ color: def.color }}>{def.label}s</span>
                <span className="detail__nums">
                  {line.totals[id]} total · {line.averages[id]} per game · best {line.highs[id]!.value}
                </span>
              </div>
              <TrendChart
                points={perGame.map((p, i) => ({ label: p.label, value: values[i]! }))}
                rolling={rollingAverage(values, 3)}
                color={def.color}
                height={90}
                ariaLabel={`${line.player.name} ${def.label} by game`}
              />
              <BarRow
                label="Share of team"
                value={Math.round(line.share[id]! * 100)}
                max={100}
                color={def.color}
                suffix="%"
              />
            </div>
          )
        })}
      </div>
    </section>
  )
}

function countFor(stats: SeasonStats, gameId: ID, playerId: ID, statId: StatTypeId): number {
  const g = stats.games.find((x) => x.game.id === gameId)
  return g?.perPlayer[playerId]?.[statId] ?? 0
}

function TeamTrend({ stats }: { stats: SeasonStats }) {
  return (
    <section className="panel">
      <h2 className="panel__title">Team by game</h2>
      {stats.statIds.map((id) => {
        const def = statType(id)
        const values = stats.games.map((g) => g.totals[id]!)
        return (
          <div key={id} className="detail__stat">
            <div className="detail__head">
              <span style={{ color: def.color }}>{def.label}s</span>
              <span className="detail__nums">
                {stats.teamAverages[id]} per game · faint line is a 3 game rolling average
              </span>
            </div>
            <TrendChart
              points={stats.games.map((g, i) => ({
                label: g.game.roundAbbreviation ?? String(i + 1),
                value: values[i]!,
              }))}
              rolling={rollingAverage(values, 3)}
              color={def.color}
              ariaLabel={`Team ${def.label} by game`}
            />
          </div>
        )
      })}
    </section>
  )
}

function WinLoss({ stats }: { stats: SeasonStats }) {
  const wl = stats.winLoss!
  return (
    <section className="panel">
      <h2 className="panel__title">Wins against losses</h2>
      <p className="muted small">
        Average per game. {wl.wins.games} {wl.wins.games === 1 ? 'win' : 'wins'}, {wl.losses.games}{' '}
        {wl.losses.games === 1 ? 'loss' : 'losses'}.
      </p>
      {stats.statIds.map((id) => {
        const def = statType(id)
        const max = Math.max(wl.wins.averages[id]!, wl.losses.averages[id]!, 1)
        return (
          <div key={id} className="wl">
            <div className="wl__title" style={{ color: def.color }}>
              {def.label}s
            </div>
            <BarRow label="In wins" value={wl.wins.averages[id]!} max={max} color="var(--good)" />
            <BarRow label="In losses" value={wl.losses.averages[id]!} max={max} color="var(--danger)" />
          </div>
        )
      })}
    </section>
  )
}

function Periods({ stats }: { stats: SeasonStats }) {
  return (
    <section className="panel">
      <h2 className="panel__title">By period</h2>
      <p className="muted small">Average per game in each half.</p>
      {stats.statIds.map((id) => {
        const def = statType(id)
        const max = Math.max(...stats.periods.map((p) => p.averages[id]!), 1)
        return (
          <div key={id} className="wl">
            <div className="wl__title" style={{ color: def.color }}>
              {def.label}s
            </div>
            {stats.periods.map((p) => (
              <BarRow key={p.label} label={p.label} value={p.averages[id]!} max={max} color={def.color} />
            ))}
          </div>
        )
      })}
    </section>
  )
}

function Opponents({ stats }: { stats: SeasonStats }) {
  return (
    <section className="panel">
      <h2 className="panel__title">By opponent</h2>
      <div className="tablewrap">
        <table className="table">
          <thead>
            <tr>
              <th>Opponent</th>
              <th>P</th>
              <th>Record</th>
              {stats.statIds.map((id) => (
                <th key={id} style={{ color: statType(id).color }}>
                  {statType(id).short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stats.opponents.map((o) => (
              <tr key={o.name}>
                <td className="table__name">{o.name}</td>
                <td>{o.games}</td>
                <td className="table__muted">
                  {o.wins}W {o.losses}L{o.draws ? ` ${o.draws}D` : ''}
                  {o.awaiting ? ` (${o.awaiting} pending)` : ''}
                </td>
                {stats.statIds.map((id) => (
                  <td key={id} className="table__num">
                    {o.averages[id]}
                    <span className={'delta' + (o.delta[id]! > 0 ? ' delta--up' : o.delta[id]! < 0 ? ' delta--down' : '')}>
                      {o.delta[id]! > 0 ? `+${o.delta[id]}` : o.delta[id]! < 0 ? o.delta[id] : ''}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">
        Average per game against each opponent, with the difference from our season average. Most of these
        rest on one or two games.
      </p>
    </section>
  )
}
